import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconCheckbox, IconChevronRight, IconGripVertical, IconLayoutKanban, IconLock, IconPencil, IconPlus, IconX } from '@tabler/icons-react';
import type { BoardStage } from '../../../../entities/board';
import { pb } from '../../../../shared/api/pocketbase';
import { Button } from '../../../../shared/ui/Button';
import { BoardModal } from '../../../../features/admin-board-form';

const pluralBoards = (n: number) => {
  if (n === 1) return '1 колонка';
  if (n < 5) return `${n} колонки`;
  return `${n} колонок`;
};

const BoardSection: React.FC = () => {
  const [stages, setStages] = useState<BoardStage[]>([]);
  const [localStages, setLocalStages] = useState<BoardStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; stage?: BoardStage; insertOrder?: number } | null>(null);

  const loadStages = useCallback(() => {
    setLoading(true);
    pb.collection('board')
      .getFullList<BoardStage>({ sort: 'order', fields: 'id,name,order,actual,colors,system,finishSuccess,finishClose' })
      .then(data => { setStages(data); setLocalStages(data); })
      .catch(() => { setStages([]); setLocalStages([]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadStages(); }, [loadStages]);
  useEffect(() => { setLocalStages(stages); }, [stages]);

  const finishMinOrder = useMemo(() => {
    const finishStages = localStages.filter(s => s.finishSuccess || s.finishClose);
    if (!finishStages.length) return null;
    return Math.min(...finishStages.map(s => s.order));
  }, [localStages]);

  const firstFinishIdx = useMemo(
    () => localStages.findIndex(s => s.finishSuccess || s.finishClose),
    [localStages],
  );

  const maxInsertIdx = firstFinishIdx === -1 ? localStages.length : firstFinishIdx;

  const computeInsertOrder = (idx: number): number => {
    if (localStages.length === 0) return 0;
    if (idx === 0) return (localStages[0].order ?? 1) - 1;
    if (idx >= localStages.length) return (localStages[localStages.length - 1].order ?? 0) + 1;
    return (localStages[idx - 1].order + localStages[idx].order) / 2;
  };

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  const handleDrop = async (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const reordered = [...localStages];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(idx, 0, moved);
    const withOrder = reordered.map((s, i) => ({ ...s, order: i }));
    setLocalStages(withOrder);
    setDragIdx(null);
    setDragOverIdx(null);
    setSavingOrder(true);
    try {
      await Promise.all(withOrder.map(s => pb.collection('board').update(s.id, { order: s.order })));
      setStages(withOrder);
    } catch {
      setLocalStages(stages);
    } finally {
      setSavingOrder(false);
    }
  };

  const openAdd = () => setModal({ mode: 'add', insertOrder: computeInsertOrder(maxInsertIdx) });
  const openEdit = (stage: BoardStage) => setModal({ mode: 'edit', stage });

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Борд</h2>
          <p className="adm-section-sub">
            {loading ? 'Загрузка...' : pluralBoards(localStages.length)}
            {savingOrder && <span className="adm-saving-hint"> · Сохранение...</span>}
          </p>
        </div>
        <Button variant="adm-add" type="button" onClick={openAdd}>
          <IconPlus size={15} stroke={2.5} />Добавить колонку
        </Button>
      </div>

      {!loading && localStages.length > 0 && (
        <div className="adm-pipeline-wrap">
          <div className="adm-pipeline-label">Последовательность</div>
          <div className="adm-pipeline">
            {localStages.map((stage, idx) => (
              <React.Fragment key={stage.id}>
                {idx > 0 && <IconChevronRight size={11} stroke={2} className="adm-pipeline-arrow" />}
                <span
                  className={`adm-pipeline-stage${!stage.actual ? ' inactive' : ''}`}
                  style={stage.colors && stage.actual
                    ? { background: stage.colors.headerBg, borderColor: stage.colors.dotBg + '88', color: stage.colors.nameColor }
                    : undefined}
                >
                  <span
                    className="adm-pipeline-num"
                    style={stage.colors && stage.actual ? { background: stage.colors.dotBg + '40' } : undefined}
                  >
                    {idx + 1}
                  </span>
                  {stage.name}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <>
          <div className="adm-drag-hint">
            <IconGripVertical size={13} stroke={2} />
            Перетащите для изменения порядка
          </div>
          <div className="adm-ut-wrap">
            <table className="adm-ut">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th>Колонка</th>
                  <th style={{ width: 130 }}>Тип</th>
                  <th style={{ width: 52 }} />
                </tr>
              </thead>
              <tbody>
                {localStages.map((stage, idx) => {
                  const isSystem = Boolean(stage.system);
                  const isFinishSuccess = Boolean(stage.finishSuccess);
                  const isFinishClose = Boolean(stage.finishClose);
                  const c = stage.colors;
                  return (
                    <tr
                      key={stage.id}
                      className={[
                        'adm-ut-row',
                        'adm-board-row',
                        !stage.actual ? 'adm-board-row--inactive' : '',
                        dragIdx === idx ? 'adm-board-row--dragging' : '',
                        dragOverIdx === idx && dragIdx !== idx ? 'adm-board-row--over' : '',
                      ].filter(Boolean).join(' ')}
                      draggable={!isSystem}
                      onDragStart={!isSystem ? () => handleDragStart(idx) : undefined}
                      onDragOver={e => handleDragOver(e, idx)}
                      onDrop={e => { void handleDrop(e, idx); }}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="adm-board-grip-cell">
                        {!isSystem && <IconGripVertical size={15} stroke={1.8} className="adm-board-grip" />}
                        {isSystem && <IconLock size={13} stroke={2} className="adm-board-lock" />}
                      </td>
                      <td>
                        <div className="adm-board-stage-info">
                          <div
                            className="adm-board-stage-swatch"
                            style={c && stage.actual ? { background: c.headerBg, borderColor: c.dotBg + '66' } : undefined}
                          >
                            {c && stage.actual
                              ? <span className="adm-board-stage-dot" style={{ background: c.dotBg }} />
                              : <IconLayoutKanban size={12} stroke={2} className="adm-board-stage-icon" />}
                          </div>
                          <div className="adm-board-stage-meta">
                            <span
                              className="adm-board-stage-name"
                              style={c && stage.actual ? { color: c.nameColor } : undefined}
                            >
                              {stage.name || 'Без названия'}
                            </span>
                            {!stage.actual && (
                              <span className="adm-board-stage-hint">Скрыта на доске</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="adm-board-badges">
                          {isSystem && <span className="adm-board-badge system"><IconLock size={9} stroke={2} />Системная</span>}
                          {isFinishSuccess && <span className="adm-board-badge finish-success"><IconCheckbox size={9} stroke={2} />Успех</span>}
                          {isFinishClose && <span className="adm-board-badge finish-close"><IconX size={9} stroke={2} />Закрытие</span>}
                          {!stage.actual && <span className="adm-board-badge inactive-badge">Откл.</span>}
                        </div>
                      </td>
                      <td className="adm-ut-actions-cell">
                        <div className="adm-ut-actions">
                          <Button variant="adm-edit" type="button" title="Редактировать" onClick={() => openEdit(stage)}>
                            <IconPencil size={13} stroke={2} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {localStages.length === 0 && (
                  <tr><td colSpan={4} className="adm-empty">Колонки не созданы</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modal && (
        <BoardModal
          mode={modal.mode}
          initial={modal.stage ?? null}
          isSystem={Boolean(modal.stage?.system)}
          finishMinOrder={finishMinOrder}
          insertOrder={modal.insertOrder}
          onClose={() => setModal(null)}
          onSaved={loadStages}
        />
      )}
    </div>
  );
};

export { BoardSection };
